/**
 * Backend Historical Reports API Service
 * 
 * Implementation following REACT_INTEGRATION.md patterns with:
 * - Email-based authentication (no JWT)
 * - Proper endpoint structure
 * - Error handling
 * - Email normalization
 */

import { apiPath } from '../config/env';
import { httpRequest } from './httpClient';
import { authManager } from './authManager';
import { normalizeEmail } from '../utils/emailUtils';
import { acquireTranslationToken } from './tokenUtils';
import {
    HistoricalReportDetail,
    HistoricalReportListResponse,
    ListReportsRequest
} from '../types/historical';/**
 * Create API call with proper authentication and error handling
 */
async function apiCall<T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<T> {
    try {
        // FastAPI typically canonicalizes paths with trailing slash. If we supply
        // just 'historical-reports?query' it may redirect to add slash. Normalize:
        let normalizedEndpoint = endpoint;
        if (normalizedEndpoint.startsWith('?')) {
            normalizedEndpoint = '/' + normalizedEndpoint; // results in 'historical-reports/?...'
        } else if (normalizedEndpoint !== '' && !normalizedEndpoint.startsWith('/')) {
            normalizedEndpoint = '/' + normalizedEndpoint;
        }
        const fullPath = apiPath(`historical-reports${normalizedEndpoint}`);

        const apiResult = await httpRequest<T>(fullPath, options);

        if (!apiResult.success) {
            throw new Error((apiResult as any).error?.message || 'API request failed');
        }
        return apiResult.data;
    } catch (err) {
        if (err instanceof Error) {
            throw err;
        }
        throw new Error('Unknown error occurred during API call');
    }
}

/**
 * Build query string from parameters, excluding undefined/null/empty values
 */
function buildQueryString(params: Record<string, string | number | undefined>): string {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, String(value));
        }
    });

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
}

/**
 * Backend Historical Reports API Service
 */
export const backendHistoricalReportService = {
    /**
     * List historical reports with filtering and pagination
     */
    async listReports(request: ListReportsRequest): Promise<HistoricalReportListResponse> {
        // Always include normalized user email (decode if it's URL-encoded)
        let userEmail = request.user_email;
        // Check if email is URL-encoded and decode it first
        if (userEmail.includes('%')) {
            try {
                userEmail = decodeURIComponent(userEmail);
            } catch {
                // If decode fails, use as-is
            }
        }

        const queryParams = {
            user_email: normalizeEmail(userEmail),
            data_object_id: request.data_object_id,
            folder_id: request.folder_id,
            status: request.status,
            from_date: request.from_date,
            to_date: request.to_date,
            order_by: request.order_by,
            order_direction: request.order_direction,
            limit: request.limit,
            offset: request.offset,
        };

        const queryString = buildQueryString(queryParams); // already includes leading ? (or empty)
        const raw = await apiCall<HistoricalReportListResponse>(`${queryString}`);
        // Normalize health_score_overall to number where possible & default is_active
        raw.reports = raw.reports.map(r => {
            let score: number | string | null = r.health_score_overall;
            if (typeof score === 'string') {
                const trimmed = score.trim().replace(/%$/, '');
                const parsed = parseFloat(trimmed);
                if (!Number.isNaN(parsed)) score = parsed; // store numeric value
            }
            return {
                ...r,
                health_score_overall: score,
                is_active: r.is_active !== undefined ? r.is_active : true
            };
        });
        return raw;
    },

    /**
     * Get detailed report by ID.
     * When targetLanguage is provided (and not 'en'), the backend returns
     * raw_report with translated content inline — no separate translate call needed.
     */
    async getReportDetail(
        reportId: string,
        userEmail: string,
        targetLanguage?: string
    ): Promise<HistoricalReportDetail> {
        let decodedEmail = userEmail;
        if (userEmail.includes('%')) {
            try {
                decodedEmail = decodeURIComponent(userEmail);
            } catch {
                // If decode fails, use as-is
            }
        }

        const queryParams: Record<string, string | number | undefined> = {
            user_email: normalizeEmail(decodedEmail),
        };

        const needsTranslation = targetLanguage && targetLanguage !== 'en';
        if (needsTranslation) {
            queryParams.target_language = targetLanguage;
        }

        const queryString = buildQueryString(queryParams);

        // Translation requires a Bearer token for the backend Translation API
        const options: RequestInit = {};
        if (needsTranslation) {
            const token = await acquireTranslationToken();
            options.headers = { 'Authorization': `Bearer ${token}` };
        }

        return apiCall<HistoricalReportDetail>(
            `/${encodeURIComponent(reportId)}${queryString}`,
            options
        );
    },
};

/**
 * Authenticated Historical Reports Service
 * Uses authManager for automatic user email retrieval
 */
export const authenticatedHistoricalReportService = {
    /**
     * List reports with automatic authentication
     */
    async listReports(filters: Omit<ListReportsRequest, 'user_email'> = {}): Promise<HistoricalReportListResponse> {
        const userEmail = authManager.getUserEmail();
        if (!userEmail) {
            throw new Error('Not authenticated - user email required');
        }

        return backendHistoricalReportService.listReports({
            user_email: userEmail,
            ...filters,
        });
    },

    /**
     * Get report detail with automatic authentication.
     * Pass targetLanguage to receive translated content inline.
     */
    async getReportDetail(reportId: string, targetLanguage?: string): Promise<HistoricalReportDetail> {
        const userEmail = authManager.getUserEmail();
        if (!userEmail) {
            throw new Error('Not authenticated - user email required');
        }

        return backendHistoricalReportService.getReportDetail(reportId, userEmail, targetLanguage);
    },
};

/**
 * Utility functions for common operations
 */
export const historicalReportUtils = {
    /**
     * Get health score trends for multiple data objects
     */
    async getHealthScoreTrends(
        userEmail: string,
        dataObjectIds: string[]
    ): Promise<Array<{
        dataObjectId: string;
        trend: Array<{
            date: string;
            healthScore: number | null;
            issueCount: number | null;
        }>;
    }>> {
        const trends = await Promise.all(
            dataObjectIds.map(async (dataObjectId) => {
                const response = await backendHistoricalReportService.listReports({
                    user_email: userEmail,
                    data_object_id: dataObjectId,
                    status: 'analyzed',
                    order_by: 'generated_at',
                    order_direction: 'asc',
                    limit: 100,
                });

                return {
                    dataObjectId,
                    trend: response.reports.map(report => {
                        let hs = report.health_score_overall as any;
                        if (typeof hs === 'string') {
                            const trimmed = hs.trim().replace(/%$/, '');
                            const parsed = parseFloat(trimmed);
                            if (!Number.isNaN(parsed)) hs = parsed; else hs = null;
                        }
                        return {
                            date: report.generated_at,
                            healthScore: (typeof hs === 'number' ? hs : null),
                            issueCount: report.issue_count,
                        };
                    }),
                };
            })
        );

        return trends;
    },

    /**
     * Retry API call with exponential backoff
     */
    async withRetry<T>(
        operation: () => Promise<T>,
        maxRetries: number = 3,
        baseDelay: number = 1000
    ): Promise<T> {
        let lastError: Error;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                return await operation();
            } catch (error) {
                lastError = error as Error;

                if (attempt === maxRetries) {
                    throw lastError;
                }

                // Exponential backoff: baseDelay * 2^(attempt-1)
                const delay = baseDelay * Math.pow(2, attempt - 1);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }

        throw lastError!;
    },
};